// src/pages/MyAccountPage.jsx
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LocationContext } from '../contexts/LocationContext';
import Header from '../components/layout/Header';
import '../styles/my-account.css';

const MyAccountPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const locationContext = useContext(LocationContext);
  const { deliveryLocation } = locationContext || {};

  const formatDate = (dateString) => {
    if (!dateString) return 'Non disponible';
    
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (err) {
      return 'Date invalide';
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Non renseigné';
    // Format : 6XX XXX XXX
    if (phone.length === 9) {
      return `${phone.substring(0, 3)} ${phone.substring(3, 6)} ${phone.substring(6)}`;
    }
    return phone;
  };

  const getPaymentMethod = () => {
    // Pour le moment, retourner "Non défini" car cette info n'est pas encore dans le user
    // Plus tard, cela viendra de user.payment_method
    return user?.payment_method || 'Non défini';
  };

  const handleBackClick = () => {
    navigate('/welcome-page');
  };

  return (
    <div className="my-account-page">
      <Header 
        title="Mon compte" 
        showBackButton={true}
        onBackClick={handleBackClick}
      />

      <div className="my-account-container">
        {/* Section profil avec icône utilisateur */}
        <div className="profile-section">
          <div className="profile-icon">
            <svg 
              width="80" 
              height="80" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h2 className="profile-name">
            {user?.prenom} {user?.nom}
          </h2>
          <p className="profile-email">{user?.email || 'Email non renseigné'}</p>
        </div>

        {/* Informations personnelles */}
        <div className="account-section">
          <h3 className="section-title">Informations personnelles</h3>
          
          <div className="info-group">
            <div className="info-item">
              <div className="info-icon">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Prénom</span>
                <span className="info-value">{user?.prenom || 'Non renseigné'}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Nom</span>
                <span className="info-value">{user?.nom || 'Non renseigné'}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-10 6L2 7" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Email</span>
                <span className="info-value">{user?.email || 'Non renseigné'}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Téléphone</span>
                <span className="info-value">{formatPhone(user?.telephone)}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Membre depuis</span>
                <span className="info-value">{formatDate(user?.date_inscription)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Adresse de livraison */}
        <div className="account-section">
          <h3 className="section-title">Adresse de livraison</h3>
          
          <div className="info-group">
            <div className="info-item">
              <div className="info-icon">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Adresse par défaut</span>
                <span className="info-value">
                  {deliveryLocation?.address || 'Aucune adresse enregistrée'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Préférences */}
        <div className="account-section">
          <h3 className="section-title">Préférences</h3>
          
          <div className="info-group">
            <div className="info-item">
              <div className="info-icon">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Langue</span>
                <span className="info-value">Français</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Moyen de paiement par défaut</span>
                <span className="info-value">{getPaymentMethod()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Note informative */}
        <div className="info-note">
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <p>
            Pour modifier vos informations personnelles, veuillez contacter notre service client.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MyAccountPage;